/** Blattgröße einschließlich Ausrichtung. */
export function sheetSize(setup) {
    const { width, height } = setup.size;
    return setup.orientation === 'landscape'
        ? { width: Math.max(width, height), height: Math.min(width, height) }
        : { width: Math.min(width, height), height: Math.max(width, height) };
}
/**
 * Inhaltsbereich innerhalb der Ränder. Feldkoordinaten sind relativ zu seiner
 * linken oberen Ecke (Anhang A.1, Grundsatz 2). Bei `duplex` werden die
 * seitlichen Ränder auf geraden Seiten gespiegelt (Seitenindex 0 = Seite 1).
 */
export function contentBox(setup, pageIndex = 0) {
    const sheet = sheetSize(setup);
    const mirrored = setup.duplex && pageIndex % 2 === 1;
    const left = mirrored ? setup.margins.right : setup.margins.left;
    const right = mirrored ? setup.margins.left : setup.margins.right;
    return {
        x: left,
        y: setup.margins.top,
        width: sheet.width - left - right,
        height: sheet.height - setup.margins.top - setup.margins.bottom,
    };
}
/** Rechteck eines Feldes in Blattkoordinaten. */
export function itemRectOnSheet(item, setup, pageIndex = 0) {
    const box = contentBox(setup, pageIndex);
    return { x: box.x + item.x, y: box.y + item.y, width: item.width, height: item.height };
}
export function rectCenter(rect) {
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}
export function rectsOverlap(a, b) {
    return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}
export function pointInRect(point, rect) {
    return (point.x >= rect.x &&
        point.x <= rect.x + rect.width &&
        point.y >= rect.y &&
        point.y <= rect.y + rect.height);
}
/** Umschließendes Rechteck mehrerer Rechtecke. */
export function boundingBox(rects) {
    if (rects.length === 0)
        return null;
    const first = rects[0];
    let minX = first.x;
    let minY = first.y;
    let maxX = first.x + first.width;
    let maxY = first.y + first.height;
    for (const rect of rects.slice(1)) {
        minX = Math.min(minX, rect.x);
        minY = Math.min(minY, rect.y);
        maxX = Math.max(maxX, rect.x + rect.width);
        maxY = Math.max(maxY, rect.y + rect.height);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
export function degToRad(degrees) {
    return (degrees * Math.PI) / 180;
}
export function radToDeg(radians) {
    return (radians * 180) / Math.PI;
}
/** Dreht `point` um `origin` (Grad, im Uhrzeigersinn wie im Dokumentmodell). */
export function rotatePoint(point, origin, degrees) {
    if (degrees === 0)
        return { ...point };
    const angle = degToRad(degrees);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    return {
        x: origin.x + dx * cos - dy * sin,
        y: origin.y + dx * sin + dy * cos,
    };
}
/** Die vier Ecken eines (gedrehten) Feldes, beginnend links oben. */
export function rectCorners(rect, rotation = 0) {
    const center = rectCenter(rect);
    const corners = [
        { x: rect.x, y: rect.y },
        { x: rect.x + rect.width, y: rect.y },
        { x: rect.x + rect.width, y: rect.y + rect.height },
        { x: rect.x, y: rect.y + rect.height },
    ];
    return corners.map((corner) => rotatePoint(corner, center, rotation));
}
/** Achsenparalleles Rechteck um ein gedrehtes Feld. */
export function rotatedBounds(rect, rotation = 0) {
    if (rotation === 0)
        return { ...rect };
    const corners = rectCorners(rect, rotation);
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}
/** Punkt in das ungedrehte Koordinatensystem eines Feldes zurückrechnen. */
export function toItemSpace(point, rect, rotation = 0) {
    return rotatePoint(point, rectCenter(rect), -rotation);
}
/** Treffer auf ein (auch gedrehtes) Feld, mit Toleranz in mm. */
export function hitTest(point, rect, rotation = 0, tolerance = 0) {
    const local = toItemSpace(point, rect, rotation);
    return pointInRect(local, {
        x: rect.x - tolerance,
        y: rect.y - tolerance,
        width: rect.width + tolerance * 2,
        height: rect.height + tolerance * 2,
    });
}
/**
 * Auswahlrechteck: ein Feld gilt als erfasst, wenn sich die Flächen
 * überschneiden (bei `contain` muss es vollständig im Rechteck liegen).
 */
export function marqueeHits(marquee, items, mode = 'touch') {
    const normalized = normalizeRect(marquee);
    return items
        .filter(({ rect, rotation }) => {
        const bounds = rotatedBounds(rect, rotation ?? 0);
        if (mode === 'contain') {
            return (bounds.x >= normalized.x &&
                bounds.y >= normalized.y &&
                bounds.x + bounds.width <= normalized.x + normalized.width &&
                bounds.y + bounds.height <= normalized.y + normalized.height);
        }
        return rectsOverlap(bounds, normalized);
    })
        .map(({ id }) => id);
}
/** Rechteck mit positiver Breite und Höhe (Ziehen in jede Richtung). */
export function normalizeRect(rect) {
    return {
        x: rect.width < 0 ? rect.x + rect.width : rect.x,
        y: rect.height < 0 ? rect.y + rect.height : rect.y,
        width: Math.abs(rect.width),
        height: Math.abs(rect.height),
    };
}
/**
 * Zoomfaktor, mit dem das Blatt vollständig in die Ansicht passt
 * („Seite einpassen“, Phase 1.1).
 */
export function fitZoom(setup, viewportPx, paddingPx = 32) {
    const sheet = sheetSize(setup);
    const available = {
        width: Math.max(viewportPx.width - paddingPx * 2, 1),
        height: Math.max(viewportPx.height - paddingPx * 2, 1),
    };
    const pxPerMm = 96 / 25.4;
    return Math.min(available.width / (sheet.width * pxPerMm), available.height / (sheet.height * pxPerMm));
}
/** Zoomstufen der Oberfläche; `clampZoom` hält Eingaben in diesem Bereich. */
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 8;
export const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
export function clampZoom(zoom) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}
/** Nächste Zoomstufe nach oben oder unten (Tastatur: Strg+Plus/Minus). */
export function nextZoomStep(zoom, direction) {
    const steps = [...ZOOM_STEPS];
    if (direction === 1) {
        return clampZoom(steps.find((step) => step > zoom + 1e-6) ?? ZOOM_MAX);
    }
    return clampZoom([...steps].reverse().find((step) => step < zoom - 1e-6) ?? ZOOM_MIN);
}
//# sourceMappingURL=geometry.js.map